import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Layers } from 'lucide-react';
import LogoAvatar from '@/components/LogoAvatar';

interface LogoToastProps {
  visible: boolean;
  logo: {
    tenantName: string;
    logoUrl: string | null;
  };
  onDismiss: () => void;
}

export default function LogoToast({ visible, logo, onDismiss }: LogoToastProps) {
  const { t } = useTranslation('scout');
  const navigate = useNavigate();

  useEffect(() => {
    if (visible) {
      const timer = setTimeout(onDismiss, 4000);
      return () => clearTimeout(timer);
    }
  }, [visible, onDismiss]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed top-4 inset-x-4 z-50"
          role="alert"
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        >
          <button
            onClick={() => {
              onDismiss();
              navigate('/collection');
            }}
            className="w-full flex items-center gap-3 p-4 rounded-2xl bg-primary/5 border border-primary/20 shadow-lg"
          >
            <LogoAvatar
              logoUrl={logo.logoUrl}
              tenantName={logo.tenantName}
              size="w-10 h-10"
              initialsSize="text-sm"
              className="bg-primary/10 flex-shrink-0"
            />
            <div className="flex-1 text-start min-w-0">
              <p className="text-caption font-medium text-muted-foreground">{t('celebrations.newLogo')}</p>
              <p className="text-small font-bold text-primary truncate">{logo.tenantName}</p>
            </div>
            <Layers className="w-5 h-5 text-primary flex-shrink-0" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
